import React, { useCallback, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import postCheckConfirmCode from '@/fetches/user/auth/post-check-confirm-code';
import postNewSocialEmail from '@/fetches/user/auth/post-new-social-email';
import postSendConfirmEmail from '@/fetches/user/auth/post-send-confirm-email';
import useAlert from '@/hooks/use-alert';
import useSelectorUser from '@/hooks/use-selector-user';
import { actions } from '@/store';
import { EAuthModalPage } from '@/store/types';
import timeSleep from '@/utils/time-sleep';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import Message from '@/constant/message';
import { useCounterDown } from '../common/use-counter-down';
import { AuthTitle } from '../common/auth-title';
import { AuthButton } from '../common/auth-button';
import { EmailBox } from '../common/email-box';
import { Anchor, AnchorText } from '../common/anchor-text';
import { InputText } from '../common/input-text';

const Time = styled(AnchorText)`
  margin: 0;
  padding: 0;
  align-self: flex-start;
  margin-bottom: 20px;
  font-size: 13px;
`;

export const SendEmailAndInputConfirmCode: React.FC = () => {
  const { email, confirmCodeType, token } = useSelectorUser('authModal');
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [sendComplete, setSendComplete] = useState(false);
  const { t, i18n } = useTranslation();
  const { alert } = useAlert();
  const dispatch = useDispatch();
  const { remainSeconds, startCountDown, stopCountDown } = useCounterDown();

  const title = useMemo(() => {
    switch (confirmCodeType) {
      default:
      case 'email': return Message.이메일인증;
      case 'social': return Message.새이메일등록;
      case 'pwreset': return Message.비밀번호재설정;
    }
  }, [confirmCodeType]);

  const displayRemainTime = useMemo(() => {
    if (remainSeconds <= 0) {
      return `${t(Message.남은시간)}: ${t(Message.시간초과)}`;
    }
    const m = Math.floor(remainSeconds / 60);
    const s = Math.floor(remainSeconds % 60);
    if (i18n.language === 'en') {
      return `${t(Message.남은시간)}: ${m} ${m !== 1 ? 'minutes' : 'minute'} ${s} ${s !== 1 ? 'seconds' : 'second'}`;
    }
    return `${t(Message.남은시간)}: ${m}${t(Message.분)} ${s}${t(Message.초)}`;
  }, [i18n.language, remainSeconds, t]);

  const onChangeCodeInput = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setCode(e.currentTarget.value.toUpperCase());
  }, []);

  const onClickSendEmailButton = useCallback(async () => {
    let success = true;
    setLoading(true);
    const result = await postSendConfirmEmail(confirmCodeType, email);
    if (!result) {
      await alert(t(Message.이메일전송에실패했습니다잠시후다시시도해주세요));
      stopCountDown();
      success = false;
    } else {
      setSendComplete(true);
      startCountDown(result);
      success = true;
    }
    await timeSleep(1000);
    setLoading(false);
    return success;
  }, [alert, confirmCodeType, email, startCountDown, stopCountDown, t]);

  const onClickSendEmailRetryButton = useCallback(async () => {
    const sendOK = await onClickSendEmailButton();
    if (sendOK) {
      await alert(t(Message.이메일을다시전송했습니다이메일보관함을확인해주세요));
    }
  }, [alert, onClickSendEmailButton, t]);

  const onClickSubmitConfirmcodeButton = useCallback(async () => {
    if (remainSeconds <= 0) {
      await alert(t(Message.인증유효시간이지났습니다다시진행해주세요));
      setCode('');
      setSendComplete(false);
      stopCountDown();
      return;
    }

    if (!code.length) {
      await alert(t(Message.인증코드를입력해주세요));
      return;
    }

    setLoading(true);
    const { data, error } = await postCheckConfirmCode({
      type: confirmCodeType,
      code,
      email,
    });
    if (error) {
      await alert(t(error));
      await timeSleep(1000);
      setLoading(false);
      return;
    }

    if (confirmCodeType === 'email') {
      await alert(t(Message.인증에성공하였습니다));
      if (data?.isAgreed) {
        dispatch(actions.user.authModal.setCurrentPage(EAuthModalPage.INPUT_NEW_PASSWORD));
      } else {
        dispatch(actions.user.authModal.setCurrentPage(EAuthModalPage.ARGREE_POLICY));
      }
    } else if (confirmCodeType === 'pwreset') {
      await alert(t(Message.인증에성공하였습니다));
      dispatch(actions.user.authModal.setCurrentPage(EAuthModalPage.FIND_PASSWORD_INPUT_NEW_PASSWORD));
    } else if (confirmCodeType === 'social') {
      const result = await postNewSocialEmail(email, token.at);
      if (result) {
        if (data?.isAgreed) {
          dispatch(actions.user.authModal.setCurrentPage(EAuthModalPage.SUCCESS));
        } else {
          dispatch(actions.user.authModal.setCurrentPage(EAuthModalPage.ARGREE_POLICY));
        }
      } else {
        await alert(t(Message.처리도중서버에서오류가발생했습니다잠시후다시시도하시거나관리자에게문의해주세요));
      }
    }

    await timeSleep(1000);
    setLoading(false);
  }, [alert, code, confirmCodeType, dispatch, email, remainSeconds, stopCountDown, token.at, t]);

  if (!sendComplete) {
    return (
      <>
        <AuthTitle>{t(title)}</AuthTitle>
        <EmailBox>{email}</EmailBox>
        <AnchorText>{t(Message.입력한이메일로인증이메일을발송합니다)}</AnchorText>
        <AuthButton disabled={loading} onClick={onClickSendEmailButton}>{t(Message.이메일보내기)}</AuthButton>
      </>
    );
  }

  return (
    <>
      <AuthTitle>{t(title)}</AuthTitle>
      <EmailBox>{email}</EmailBox>
      <AnchorText>{t(Message.이메일로전송된인증코드를입력하여인증을완료해주세요)}</AnchorText>
      <InputText
        type="text"
        title={t(Message.인증코드입력)}
        value={code}
        disabled={loading}
        autoFocus
        onPressEnter={onClickSubmitConfirmcodeButton}
        onChange={onChangeCodeInput}
      />
      <Time>
        {displayRemainTime}
      </Time>
      <AuthButton
        type="button"
        onClick={onClickSubmitConfirmcodeButton}
        disabled={loading}
      >
        {t(Message.확인)}
      </AuthButton>
      <AnchorText>{t(Message.이메일을받지못했나요)}</AnchorText>
      <Anchor
        onClick={onClickSendEmailRetryButton}
        data-disabled={loading}
      >
        {t(Message.이메일재전송)}
      </Anchor>
    </>
  );
};
